import { useEffect, useRef, useState } from 'react';
import { BookmarkPlus, Check, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { generatePresentationText } from '@/lib/ai-presentation';
import { savePresentationExample, updateProject } from '@/lib/api';
import { buildExampleTitle } from '@/lib/text-library-format';
import type { Client, GiftPackage, Project } from '@/lib/types';

interface Props {
  project: Project;
  pkg: GiftPackage;
  client: Client | null;
  onSaved?: (text: string) => void;
}

/**
 * PresentationEditor — verhalende klant-tekst voor PDF.
 * - Save-on-blur naar projects.client_presentation_text
 * - "Genereer met AI" als de tekst leeg is
 * - "Regenereer" om opnieuw te beginnen vanaf de huidige inhoud
 * - Newlines / alinea's worden bewaard (plain text textarea, monospace nee)
 */
export function PresentationEditor({ project, pkg, client, onSaved }: Props) {
  const [text, setText] = useState(project.client_presentation_text ?? '');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const lastSavedRef = useRef<string>(project.client_presentation_text ?? '');

  const [uitgangspunten, setUitgangspunten] = useState((project.uitgangspunten ?? ''));
  const lastSavedUitgangspuntenRef = useRef<string>((project.uitgangspunten ?? ''));

  const [savingExample, setSavingExample] = useState(false);
  const [exampleSaved, setExampleSaved] = useState(false);

  // Sync state als project verandert (bv. na regen of nieuwe pkg gekozen)
  useEffect(() => {
    setText(project.client_presentation_text ?? '');
    lastSavedRef.current = project.client_presentation_text ?? '';
    setUitgangspunten((project.uitgangspunten ?? ''));
    lastSavedUitgangspuntenRef.current = (project.uitgangspunten ?? '');
  }, [project.id, project.client_presentation_text]);

  async function persist(value: string) {
    if (value === lastSavedRef.current) return;
    setSaving(true);
    setError(null);
    try {
      await updateProject(project.id, { client_presentation_text: value });
      lastSavedRef.current = value;
      setSavedAt(new Date());
      onSaved?.(value);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  }

  async function persistUitgangspunten(value: string) {
    if (value === lastSavedUitgangspuntenRef.current) return;
    setError(null);
    try {
      // Save-on-blur, zelfde patroon als de presentatietekst hierboven.
      await updateProject(project.id, { uitgangspunten: value });
      lastSavedUitgangspuntenRef.current = value;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uitgangspunten opslaan mislukt');
    }
  }

  async function handleSaveExample() {
    const body = text.trim();
    if (!body) return;
    setSavingExample(true);
    setExampleSaved(false);
    setError(null);
    try {
      await savePresentationExample({
        title: buildExampleTitle({
          projectName: project.name,
          clientName: client?.company_name,
          theme: pkg.theme,
        }),
        body,
      });
      setExampleSaved(true);
      setTimeout(() => setExampleSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Voorbeeld opslaan mislukt');
    } finally {
      setSavingExample(false);
    }
  }

  async function handleGenerate(replace: boolean) {
    setGenerating(true);
    setError(null);
    try {
      const generated = await generatePresentationText({
        project,
        pkg,
        client,
        items: pkg.items ?? [],
        extraContext: replace ? undefined : text || undefined,
      });
      setText(generated);
      await persist(generated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI-tekst genereren mislukt');
    } finally {
      setGenerating(false);
    }
  }

  const hasText = text.trim().length > 0;
  const wordCount = hasText ? text.trim().split(/\s+/).length : 0;

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <h3 className="text-sm font-semibold text-bmade-900 dark:text-white">
          Uitgangspunten klant
        </h3>
        <p className="text-[11px] text-bmade-500">
          Randvoorwaarden van de klant: dieet, geen alcohol, duurzaam, budget. Stuurt de toon van de
          AI-tekst.
        </p>
        <textarea
          value={uitgangspunten}
          onChange={(e) => setUitgangspunten(e.target.value)}
          onBlur={() => void persistUitgangspunten(uitgangspunten)}
          placeholder="Bv. vegetarisch, geen alcohol, 80/20 gezond, duurzaam, budget rond 35 euro per pakket."
          rows={3}
          className="w-full resize-y rounded-lg border border-bmade-200 bg-white p-3 font-sans text-sm leading-relaxed text-bmade-900 outline-none transition-colors placeholder:text-bmade-400 focus:border-accent-400 dark:border-bmade-700 dark:bg-bmade-950 dark:text-white"
          spellCheck
          lang="nl"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-bmade-900 dark:text-white">
            Tekst voor de klant
          </h3>
          <p className="mt-0.5 text-[11px] text-bmade-500">
            Verhalende presentatie, geen prijzen of tabellen. Komt bovenaan de PDF.
          </p>
        </div>
        <div className="flex gap-2">
          {!hasText ? (
            <button
              type="button"
              onClick={() => void handleGenerate(true)}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-600 disabled:opacity-40"
            >
              {generating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              Genereer met AI
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleGenerate(true)}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-md border border-bmade-300 bg-white px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-accent-400 hover:text-accent-700 disabled:opacity-40 dark:border-bmade-700 dark:bg-bmade-900 dark:text-bmade-200"
              title="Volledig nieuwe tekst genereren (overschrijft huidige)"
            >
              {generating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Regenereer
            </button>
          )}
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => void persist(text)}
        placeholder="Typ hier de tekst voor de klant, of klik 'Genereer met AI' voor een beginversie..."
        rows={14}
        className="w-full resize-y rounded-lg border border-bmade-200 bg-white p-3 font-sans text-sm leading-relaxed text-bmade-900 outline-none transition-colors placeholder:text-bmade-400 focus:border-accent-400 dark:border-bmade-700 dark:bg-bmade-950 dark:text-white"
        spellCheck
        lang="nl"
      />

      {hasText && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleSaveExample()}
            disabled={savingExample}
            className="inline-flex items-center gap-2 rounded-md border border-bmade-300 bg-white px-3 py-1.5 text-xs font-medium text-bmade-700 transition-colors hover:border-accent-400 hover:text-accent-700 disabled:opacity-40 dark:border-bmade-700 dark:bg-bmade-900 dark:text-bmade-200"
            title="Deze tekst opslaan als toon-referentie voor de generator"
          >
            {savingExample ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : exampleSaved ? (
              <Check className="h-3 w-3 text-accent-600" />
            ) : (
              <BookmarkPlus className="h-3 w-3" />
            )}
            {exampleSaved ? 'Opgeslagen als voorbeeld' : 'Bewaar als huisstijl-voorbeeld'}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-bmade-500">
        <span>{wordCount > 0 ? `${wordCount} woorden` : 'Nog geen tekst'}</span>
        <span>
          {saving
            ? 'Opslaan...'
            : savedAt
              ? `Opgeslagen ${savedAt.toLocaleTimeString('nl-NL', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : text === lastSavedRef.current
                ? 'Synchroon'
                : 'Wijzigingen niet opgeslagen (klik buiten het veld)'}
        </span>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}
